import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Eye, Users, MailCheck, MousePointerClick, Info, Sparkles, Loader2, MessagesSquare, Trophy, TrendingUp, TrendingDown, Minus, Mail, Heart } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { SimulatedBanner } from "@/components/SimulatedBanner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDateTime, formatPeriodLabel } from "@/lib/format";
import { Sparkline } from "@/components/Sparkline";
import { PlanBadge } from "@/components/PlanBadge";
import { openUpgrade } from "@/lib/planFeatures";
import { SharedReaderAnalytics } from "@/components/SharedReaderAnalytics";

const StatusChip = ({ status, id }) => {
  const map = {
    sent: "border-emerald-200 bg-emerald-50 text-emerald-700",
    simulated: "border-amber-200 bg-amber-50 text-amber-800",
    queued: "border-slate-200 bg-slate-50 text-slate-700",
    failed: "border-red-200 bg-red-50 text-red-700",
  };
  return <Badge data-testid={`delivery-status-${id}`} className={map[status] || map.queued}>{status}</Badge>;
};

const DeliveredCell = ({ d }) => {
  if (d.delivered_at) {
    return (
      <span className="inline-flex items-center gap-1.5 text-emerald-700" data-testid={`delivered-${d.id}`}>
        <MailCheck className="h-3.5 w-3.5" />
        <span className="text-xs">{formatDateTime(d.delivered_at)}</span>
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground" data-testid={`delivered-${d.id}`}>-</span>;
};

const OpenedCell = ({ d }) => {
  if (d.tracking_enabled === false) return <span data-testid={`opened-${d.id}`} className="text-xs text-muted-foreground">Tracking off</span>;
  if (!d.opened_at && d.open_count > 0) return <span data-testid={`opened-${d.id}`} className="text-xs text-muted-foreground">{d.open_count} opens · timestamps off</span>;
  if (d.opened_at) {
    return (
        <span className="inline-flex items-center gap-1.5 text-emerald-700" data-testid={`opened-${d.id}`}>
        <Eye className="h-3.5 w-3.5" />
        <span className="text-xs">{formatDateTime(d.opened_at)}</span>
        {d.open_count > 1 && (
          <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px] font-normal">×{d.open_count}</Badge>
        )}
      </span>
    );
  }
  return (
    <Badge className="border-slate-200 bg-slate-50 font-normal text-slate-500" data-testid={`opened-${d.id}`}>
      Not opened
    </Badge>
  );
};

export default function Delivery() {
  const { company } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [engagement, setEngagement] = useState([]);
  const [investorEngagement, setInvestorEngagement] = useState({ investors: [], totals: {} });
  const [engagementGated, setEngagementGated] = useState(false);
  const [digest, setDigest] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  const loadDigest = async () => {
    if (!company) return;
    try { const { data } = await api.get(`/companies/${company.id}/digest/preview`); setDigest(data); }
    catch (e) { console.error("Failed to load digest preview", e); }
  };

  useEffect(() => {
    if (!company) return;
    (async () => {
      setLoading(true);
      try {
        const [d, cfg, upds, ie] = await Promise.all([
          api.get(`/companies/${company.id}/deliveries`),
          api.get(`/config`),
          api.get(`/companies/${company.id}/updates`),
          api.get(`/companies/${company.id}/engagement/investors`, { quiet402: true }).catch((e) => ({ data: { investors: [], totals: {} }, gated: e?.response?.status === 402 })),
        ]);
        setDeliveries(d.data);
        setEmailEnabled(cfg.data.email_enabled);
        setInvestorEngagement(ie.data);
        setEngagementGated(Boolean(ie.gated));
        const published = upds.data.filter((u) => u.status === "published");
        const views = await Promise.all(published.map((u) => api.get(`/updates/${u.id}/views`).then((r) => ({ u, views: r.data.length })).catch(() => ({ u, views: 0 }))));
        const byUpdate = {};
        const opensByUpdate = {};
        d.data.forEach((row) => {
          byUpdate[row.update_id] = (byUpdate[row.update_id] || 0) + 1;
          if (row.opened_at || row.open_count > 0) opensByUpdate[row.update_id] = (opensByUpdate[row.update_id] || 0) + 1;
        });
        setEngagement(views.map(({ u, views }) => ({ update: u, recipients: byUpdate[u.id] || 0, views, opens: opensByUpdate[u.id] || 0 })));
      } catch { toast.error("Could not load delivery data. Please try again."); } finally { setLoading(false); }
      loadDigest();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/digest/send`);
      if (data.sent) toast.success(`Digest sent to ${data.recipients} investor(s) - ${data.updates_count} update(s), ${data.comments_count} comment(s)`);
      else toast.info(data.reason === "no investors" ? "Add investors to send a digest" : "Nothing new to send this week");
      loadDigest();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send digest"); }
    finally { setSendingDigest(false); }
  };

  const [sendingTest, setSendingTest] = useState(false);
  const [insights, setInsights] = useState([]);
  const [growth, setGrowth] = useState(null);
  useEffect(() => {
    if (!company) return;
    api.get(`/companies/${company.id}/reaction-insights`).then(({ data }) => setInsights(data)).catch(() => {});
    api.get(`/companies/${company.id}/growth`).then(({ data }) => setGrowth(data)).catch(() => {});
  }, [company]);
  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/digest/send-test`);
      toast.success(`Test digest emailed to ${data.to}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send test email"); }
    finally { setSendingTest(false); }
  };

  const counts = deliveries.reduce((acc, d) => { acc[d.status] = (acc[d.status] || 0) + 1; return acc; }, {});
  const deliveredCount = deliveries.filter((d) => d.delivered_at).length;
  const openedCount = deliveries.filter((d) => d.opened_at || d.open_count > 0).length;
  const openRate = deliveredCount > 0 ? Math.round((openedCount / deliveredCount) * 100) : 0;

  const Chip = ({ label, value, hint, className, "data-testid": testId }) => (
    <Card data-testid={testId} className={`rounded-xl border p-4 ${className}`}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {label}
        {hint && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild><Info className="h-3 w-3 cursor-help opacity-60" /></TooltipTrigger>
              <TooltipContent className="max-w-[240px] text-xs">{hint}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="h-display mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </Card>
  );

  return (
    <AppShell title="Delivery & Engagement" subtitle="Track how updates reach and land with investors">
      {!emailEnabled && <div className="mb-6"><SimulatedBanner /></div>}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Chip label="Delivered" value={deliveredCount} hint="Recipients we confirmed the update was sent to. With a live email provider this reflects real delivery." data-testid="chip-delivered" />
        <Chip label="Opened" value={openedCount} hint="Recipients who loaded the update in their email client (tracking pixel). Real opens only." data-testid="chip-opened" />
        <Chip label="Open rate" value={`${openRate}%`} hint="Opened ÷ Delivered." data-testid="chip-open-rate" />
        <Chip label="Portal views" value={engagement.reduce((s, e) => s + e.views, 0)} hint="Times investors opened the update inside the portal." data-testid="chip-views" />
      </div>

      <SharedReaderAnalytics companyId={company?.id} updates={engagement.map((item) => item.update)} />

      {!emailEnabled && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800" data-testid="tracking-simulation-note">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Email is in <strong>simulation</strong> mode, so no message actually leaves the app - email <strong>opens will stay empty</strong> until a real provider is connected. Delivery is recorded as simulated, and <strong>Portal views</strong> above are genuine engagement.</span>
        </div>
      )}

        <Card className="mt-6 flex flex-col gap-3 rounded-xl border border-brand/25 bg-brand/[0.05] p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="growth-card">
        <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand-ink"><Sparkles className="h-5 w-5" /></div>
          <div>
            <div className="text-sm font-semibold text-foreground">Your updates are spreading TenX</div>
            <p className="text-xs text-muted-foreground">
              {growth?.total_clicks > 0
                ? `${growth.unique_clickers} investor${growth.unique_clickers === 1 ? "" : "s"} clicked the “Sent and Powered by TenX” link in your updates${growth.last_click_at ? ` · last ${formatDateTime(growth.last_click_at)}` : ""}.`
                : "Every update email includes a “Sent and Powered by TenX” link. Clicks appear here when engagement tracking is enabled."}
            </p>
          </div>
        </div>
        <div className="text-right">
            <div className="h-display text-2xl font-semibold tabular-nums text-brand-ink" data-testid="growth-clicks">{growth?.total_clicks ?? 0}</div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">footer clicks</div>
        </div>
      </Card>

      <Card className="mt-6 flex flex-col gap-3 rounded-xl border bg-card p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="digest-card">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><MessagesSquare className="h-5 w-5" /></div>
          <div>
            <div className="text-sm font-semibold text-foreground">Weekly investor digest</div>
            <p className="text-xs text-muted-foreground">
              {digest
                ? `${digest.updates.length} update(s) and ${digest.comments_count} comment(s) in the last ${digest.window_days} days · ${digest.recipients_count} recipient(s)`
                : "A weekly summary of new updates and comments, sent to your investors."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)} className="gap-2" data-testid="preview-digest">
            <Eye className="h-4 w-4" /> Preview
          </Button>
          <Button onClick={sendDigest} disabled={sendingDigest} className="gap-2" data-testid="send-digest-now">
            {sendingDigest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send digest now
          </Button>
        </div>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg" data-testid="digest-preview-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MessagesSquare className="h-5 w-5 text-primary" /> Weekly digest preview</DialogTitle></DialogHeader>
          {digest ? (
            <div className="space-y-4">
              <div className="rounded-lg border bg-secondary/40 p-3 text-xs text-muted-foreground">
                <div><span className="font-medium text-foreground">Subject:</span> {digest.subject}</div>
                <div className="mt-1"><span className="font-medium text-foreground">Recipients:</span> {digest.recipients_count} investor(s){digest.recipients?.length ? ` - ${digest.recipients.map((r) => r.name).join(", ")}` : ""}</div>
              </div>
              <div className="rounded-xl border bg-card p-5">
                <div className="h-display text-lg font-semibold text-primary">{company?.name} weekly digest</div>
                <p className="mt-1 text-sm text-muted-foreground">{digest.updates.length} new update(s) and {digest.comments_count} new comment(s) this week.</p>
                <ul className="mt-3 space-y-1.5">
                  {digest.updates.length === 0 ? (
                    <li className="text-sm text-muted-foreground">No new updates this week.</li>
                  ) : digest.updates.map((u) => (
                    <li key={u.id} className="flex items-center gap-2 text-sm" data-testid={`digest-update-${u.id}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                      <span className="font-medium text-foreground">{u.title}</span>
                      <span className="text-xs text-muted-foreground">{formatPeriodLabel(u.period)}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-muted-foreground">Investors are prompted to sign in to their portal to read more.</p>
              </div>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={sendTest} disabled={sendingTest} data-testid="digest-send-test">
                {sendingTest ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Email me a test
              </Button>
              {!emailEnabled && <p className="text-xs text-amber-700">Email is simulated - this is exactly what will be recorded/sent once a provider is connected.</p>}
            </div>
          ) : <p className="text-sm text-muted-foreground">Loading…</p>}
        </DialogContent>
      </Dialog>

      {(investorEngagement.investors || []).some((r) => r.score > 0) && (
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            <h2 className="h-display text-lg font-semibold text-foreground">Engagement leaderboard</h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {investorEngagement.investors.filter((r) => r.score > 0).slice(0, 6).map((r) => {
              const medal = r.rank === 1 ? "bg-amber-100 text-amber-700 border-amber-200"
                : r.rank === 2 ? "bg-slate-100 text-slate-600 border-slate-200"
                : r.rank === 3 ? "bg-orange-100 text-orange-700 border-orange-200"
                : "bg-secondary text-muted-foreground border-transparent";
              return (
                <Card key={r.investor_id} className="flex items-center gap-3 rounded-xl border bg-card p-4" data-testid={`leaderboard-${r.investor_id}`}>
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${medal}`}>#{r.rank}</div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.opened} opens · {r.portal_views} views · {r.comments} comments</div>
                    <div className="mt-0.5 flex items-center gap-1 text-[11px]" data-testid={`trend-${r.investor_id}`}>
                      {r.trend === "up" ? <span className="inline-flex items-center gap-0.5 text-emerald-600"><TrendingUp className="h-3 w-3" /> Rising</span>
                        : r.trend === "down" ? <span className="inline-flex items-center gap-0.5 text-red-500"><TrendingDown className="h-3 w-3" /> Cooling</span>
                        : <span className="inline-flex items-center gap-0.5 text-muted-foreground"><Minus className="h-3 w-3" /> Steady</span>}
                      <span className="text-muted-foreground">vs last 30d</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="h-display text-lg font-semibold tabular-nums text-primary">{r.score}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">score</div>
                    {r.sparkline?.some((v) => v > 0) && <div className="mt-1 flex justify-end" data-testid={`sparkline-${r.investor_id}`}><Sparkline data={r.sparkline} /></div>}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="h-display text-lg font-semibold text-foreground">Per-investor engagement</h2>
          {engagementGated && <PlanBadge plan="growth" />}
          {investorEngagement.totals?.investors > 0 && (
            <Badge variant="secondary" className="font-normal" data-testid="engaged-count">
              {investorEngagement.totals.engaged}/{investorEngagement.totals.investors} engaged
            </Badge>
          )}
        </div>
        {engagementGated ? (
          <Card className="rounded-xl border border-dashed bg-card p-8 text-center" data-testid="engagement-upgrade-card">
            <p className="text-sm font-semibold text-foreground">Per-investor engagement is a Growth feature</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">See exactly who opens, reads and comments on every update - and who's gone quiet.</p>
            <Button size="sm" className="mt-4" onClick={() => openUpgrade({ plan: "growth" })} data-testid="engagement-upgrade-btn">
              Upgrade to Growth
            </Button>
          </Card>
        ) : loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (investorEngagement.investors || []).length === 0 ? (
          <EmptyState icon={Users} title="No investors yet" description="Add investors to see who opens and reads your updates." />
        ) : (
          <Card className="overflow-hidden rounded-xl border bg-card">
            <div className="overflow-x-auto">
              <Table data-testid="investor-engagement-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Investor</TableHead>
                    <TableHead className="text-right">Delivered</TableHead>
                    <TableHead className="text-right">Opened</TableHead>
                    <TableHead className="text-right">Open rate</TableHead>
                    <TableHead className="text-right">Portal views</TableHead>
                    <TableHead className="text-right">Comments</TableHead>
                    <TableHead className="text-right">Last activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {investorEngagement.investors.map((r) => {
                    const last = [r.last_opened_at, r.last_viewed_at].filter(Boolean).sort().slice(-1)[0];
                    return (
                      <TableRow key={r.investor_id} data-testid={`investor-engagement-row-${r.investor_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{r.name}</span>
                            {r.engaged
                              ? <Badge className="border-emerald-200 bg-emerald-50 font-normal text-emerald-700">Engaged</Badge>
                              : <Badge className="border-slate-200 bg-slate-50 font-normal text-slate-500">Quiet</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.delivered}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.opened}{r.opens_total > r.opened ? <span className="text-xs text-muted-foreground"> ({r.opens_total})</span> : null}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.open_rate}%</TableCell>
                        <TableCell className="text-right tabular-nums">{r.portal_views}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.comments}</TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">{last ? formatDateTime(last) : "-"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      <div className="mt-8">
        <h2 className="mb-3 h-display text-lg font-semibold text-foreground">Engagement by update</h2>
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : engagement.length === 0 ? (
          <EmptyState icon={Send} title="No published updates yet" description="Publish an update to start tracking delivery and opens." />
        ) : (
          <Card className="overflow-hidden rounded-xl border bg-card">
            <div className="overflow-x-auto">
              <Table data-testid="engagement-table">
                <TableHeader>
                  <TableRow><TableHead>Update</TableHead><TableHead>Period</TableHead><TableHead className="text-right">Recipients</TableHead><TableHead className="text-right">Opened</TableHead><TableHead className="text-right">Portal views</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {engagement.map(({ update, recipients, views, opens }) => (
                    <TableRow key={update.id} data-testid={`engagement-row-${update.id}`}>
                      <TableCell className="font-medium">{update.title}</TableCell>
                      <TableCell>{formatPeriodLabel(update.period)}</TableCell>
                      <TableCell className="text-right tabular-nums"><span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-muted-foreground" />{recipients}</span></TableCell>
                      <TableCell className="text-right tabular-nums"><span className="inline-flex items-center gap-1"><MousePointerClick className="h-3.5 w-3.5 text-muted-foreground" />{opens}</span></TableCell>
                      <TableCell className="text-right tabular-nums"><span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-muted-foreground" />{views}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      <div className="mt-8">
        <h2 className="mb-3 h-display text-lg font-semibold text-foreground">Delivery log</h2>
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : deliveries.length === 0 ? (
          <EmptyState icon={Send} title="No deliveries yet" description="When you publish an update, per-recipient delivery records show up here." />
        ) : (
          <Card className="overflow-hidden rounded-xl border bg-card">
            <div className="overflow-x-auto">
              <Table data-testid="deliveries-table">
                <TableHeader>
                  <TableRow><TableHead>Recipient</TableHead><TableHead>Update</TableHead><TableHead>Status</TableHead><TableHead>Delivered</TableHead><TableHead>Opened</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {deliveries.map((d) => (
                    <TableRow key={d.id} data-testid={`delivery-row-${d.id}`}>
                      <TableCell><div className="font-medium">{d.recipient_name}</div><div className="text-xs text-muted-foreground">{d.recipient_email}</div></TableCell>
                      <TableCell>{d.update_title}</TableCell>
                      <TableCell><StatusChip status={d.status} id={d.id} /></TableCell>
                      <TableCell><DeliveredCell d={d} /></TableCell>
                      <TableCell><OpenedCell d={d} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}

        {insights.length > 0 && (
          <Card className="rounded-xl border bg-card p-5" data-testid="reaction-insights">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground"><Heart className="h-4 w-4 text-primary" /> Investor reactions</h2>
            <div className="space-y-2">
              {insights.map((r) => (
                <div key={r.update_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3" data-testid={`insight-row-${r.update_id}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{r.title}</div>
                    <div className="text-xs text-muted-foreground">{r.reactors.map((x) => x.name).filter(Boolean).join(", ")}</div>
                  </div>
                  <div className="flex items-center gap-2 text-xs tabular-nums text-muted-foreground">
                    {r.counts.thumbs_up > 0 && <span>{r.counts.thumbs_up} like</span>}
                    {r.counts.heart > 0 && <span>{r.counts.heart} love</span>}
                    {r.counts.celebrate > 0 && <span>{r.counts.celebrate} celebrate</span>}
                    {r.counts.insightful > 0 && <span>{r.counts.insightful} insightful</span>}
                    <span className="font-semibold text-primary">{r.total} total</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
